import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import apiService from '../services/apiService';

const NaturalLanguageSearch = ({ 
  onSearchResults, 
  onError, 
  onLoading,
  onResetResults
}) => {
  const [searchData, setSearchData] = useState({
    query_text: '',
    location: '',
    ctc_min: '',
    ctc_max: '',
    exp_min: '',
    exp_max: '',
    limit: 5
  });
  
  const [locations, setLocations] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const locationInputRef = useRef(null);
  const [isQueryExpanded, setIsQueryExpanded] = useState(false);

  useEffect(() => {
    // Load locations from CSV file
    fetch('/loc.csv')
      .then(response => response.text())
      .then(csvText => {
        Papa.parse(csvText, {
          complete: (results) => {
            setLocations(results.data[0]);
          }
        });
      })
      .catch(err => console.error('Failed to load locations:', err));
  }, []);

  // Filter locations as user types
  useEffect(() => {
    if (searchData.location) {
      const filtered = locations
        .filter(loc =>
          loc.toLowerCase().includes(searchData.location.toLowerCase())
        );
      setFilteredLocations(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [searchData.location, locations]);

  // Hide suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        locationInputRef.current &&
        !locationInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();

    if (!searchData.query_text.trim()) {
      onError('Please enter a search query');
      return;
    }

    onLoading(true);
    onError('');

    const requestData = {
      query_text: searchData.query_text,
      location: searchData.location || null,
      ctc_min: searchData.ctc_min ? parseFloat(searchData.ctc_min) : null,
      ctc_max: searchData.ctc_max ? parseFloat(searchData.ctc_max) : null,
      exp_min: searchData.exp_min ? parseFloat(searchData.exp_min) : null,
      exp_max: searchData.exp_max ? parseFloat(searchData.exp_max) : null,
      limit: 200
    };
    try {
      const data = await apiService.nlpSearch(requestData);
      onSearchResults(data, requestData);
    } catch (err) {
      onError(`Search failed: ${err.message}`);
      console.error('Search error:', err);
    } finally {
      onLoading(false);
    }
  };



  const resetForm = () => {
    setSearchData({
      query_text: '',
      location: '',
      ctc_min: '',
      ctc_max: '',
      exp_min: '',
      exp_max: '',
      limit: 5
    });
    onResetResults();
    onError('');
  };

  return (
    <div className="search-section">
      <form onSubmit={handleSearch} className="search-form">
        <div className="form-group">
          <label htmlFor="query_text" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>🛠️ Skills & Requirements *</span>
            <button
              type="button"
              onClick={() => setIsQueryExpanded(!isQueryExpanded)}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                ':hover': {
                  backgroundColor: '#f0f8ff'
                }
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f8ff'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <span style={{
                transform: isQueryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
                fontSize: '1rem'
              }}>
                ▼
              </span>
              <span>{isQueryExpanded ? 'Collapse' : 'Expand'}</span>
            </button>
          </label>
          {isQueryExpanded ? (
            <textarea
              id="query_text"
              name="query_text"
              value={searchData.query_text}
              onChange={handleInputChange}
              placeholder="e.g., React developer with experience in fintech, Node.js backend development, API integration experience, startup environment preferred..."
              required
              rows={4}
              style={{
                resize: 'vertical',
                minHeight: '100px',
                fontFamily: 'inherit'
              }}
            />
          ) : (
            <input
              type="text"
              id="query_text"
              name="query_text"
              value={searchData.query_text}
              onChange={handleInputChange}
              placeholder="e.g., React developer with experience in fintech"
              required
            />
          )}
        </div>

        <div className="filters-grid">


          <div className="form-group">
            <label htmlFor="ctc_min">💰 Min CTC (LPA)</label>
            <input
              type="number"
              id="ctc_min"
              name="ctc_min"
              value={searchData.ctc_min}
              onChange={handleInputChange}
              placeholder="e.g., 5"
              step="0.1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ctc_max">💰 Max CTC (LPA)</label>
            <input
              type="number"
              id="ctc_max"
              name="ctc_max"
              value={searchData.ctc_max}
              onChange={handleInputChange}
              placeholder="e.g., 15"
              step="0.1"
            />
          </div>

          <div className="form-group" ref={locationInputRef} style={{ position: 'relative' }}>
            <label htmlFor="location">📍 Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={searchData.location}
              onChange={handleInputChange}
              placeholder="e.g., Bangalore, Mumbai"
              autoComplete="off"
              onFocus={() => setShowSuggestions(filteredLocations.length > 0)}
            />
            {showSuggestions && (
              <ul className="location-suggestions">
                {filteredLocations.map((loc, idx) => (
                  <li
                    key={idx}
                    onClick={() => {
                      setSearchData(prev => ({ ...prev, location: loc }));
                      setShowSuggestions(false);
                    }}
                  >
                    {loc}
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="exp_min">🧑‍💼 Min Experience (Years)</label>
            <input
              type="number"
              id="exp_min"
              name="exp_min"
              value={searchData.exp_min}
              onChange={handleInputChange}
              placeholder="e.g., 2"
              step="0.1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="exp_max">🧑‍💼 Max Experience (Years)</label>
            <input
              type="number"
              id="exp_max"
              name="exp_max"
              value={searchData.exp_max}
              onChange={handleInputChange}
              placeholder="e.g., 8"
              step="0.1"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="search-btn">
            🔍 Search Candidates
          </button>
          <button type="button" onClick={resetForm} className="reset-btn">
            🗑️ Reset
          </button>
        </div>
      </form>

      {/* Load More functionality is handled by parent component */}
    </div>
  );
};

export default NaturalLanguageSearch;

